const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const FEISTEL_KEYS = [
  0x5f3759dfn,
  0x85ebca6bn,
  0xc2b2ae35n,
  0x27d4eb2fn,
  0x165667b1n,
  0x9e3779b9n,
];

export type ServerInfo = { ip: string; port: number };

// --- Bit Manipulation & Packing ---

const packIPv4 = (ip: string, port: number): bigint => {
  const octets = ip.split('.').map(Number);
  const ipInt =
    (BigInt(octets[0]!) << 24n) |
    (BigInt(octets[1]!) << 16n) |
    (BigInt(octets[2]!) << 8n) |
    BigInt(octets[3]!);
  return (ipInt << 16n) | BigInt(port & 0xffff);
};

const unpackIPv4 = (val: bigint): ServerInfo => {
  const ipInt = val >> 16n;
  const port = Number(val & 0xffffn);
  const ip = [
    (ipInt >> 24n) & 0xffn,
    (ipInt >> 16n) & 0xffn,
    (ipInt >> 8n) & 0xffn,
    ipInt & 0xffn,
  ].join('.');
  return { ip, port };
};

const packIPv6 = (ip: string, port: number): bigint => {
  // Simple normalization: expand :: to full zeros
  let fullIp = ip;
  if (ip.includes('::')) {
    const parts = ip.split('::');
    const missing =
      8 -
      (parts[0]!.split(':').filter(Boolean).length +
        parts[1]!.split(':').filter(Boolean).length);
    fullIp = [
      ...parts[0]!.split(':'),
      ...Array(missing).fill('0'),
      ...parts[1]!.split(':'),
    ]
      .filter(Boolean)
      .join(':');
  }
  const hex = fullIp
    .split(':')
    .map(s => s.padStart(4, '0'))
    .join('');
  return (BigInt(`0x${hex}`) << 16n) | BigInt(port & 0xffff);
};

const unpackIPv6 = (val: bigint): ServerInfo => {
  const port = Number(val & 0xffffn);
  const ipInt = val >> 16n;
  const hex = ipInt.toString(16).padStart(32, '0');
  const segments: string[] = [];
  for (let i = 0; i < 32; i += 4) segments.push(hex.substring(i, i + 4));
  return {
    ip: segments
      .join(':')
      .replace(/(^|:)0+(?=[^:]|:|$)/g, '$1')
      .replace(/:0(:0)+/, '::'),
    port,
  };
};

// --- Feistel Scrambling ---

const mix = (val: bigint, key: bigint, halfWidth: bigint): bigint => {
  let h = (val ^ key) & ((1n << halfWidth) - 1n);
  h = (h * 0x85ebca6bn) & ((1n << halfWidth) - 1n);
  h ^= h >> 13n;
  h = (h * 0xc2b2ae35n) & ((1n << halfWidth) - 1n);
  return h ^ (h >> 16n);
};

const feistel = (input: bigint, bits: number, decrypt = false): bigint => {
  const halfWidth = BigInt(bits / 2);
  const mask = (1n << halfWidth) - 1n;
  let L = input >> halfWidth;
  let R = input & mask;

  const keys = decrypt ? [...FEISTEL_KEYS].reverse() : FEISTEL_KEYS;
  for (const key of keys) {
    const nextL = R;
    const nextR = L ^ (mix(R, key, halfWidth) & mask);
    L = nextL;
    R = nextR;
  }
  return (R << halfWidth) | L; // Final swap
};

// --- Checksum & Base58 ---

const getChecksum = (val: bigint, bits: number): bigint => {
  let ck = 0n;
  const nibbles = BigInt(bits / 4);
  for (let i = 0n; i < nibbles; i++) ck ^= (val >> (i * 4n)) & 0xfn;
  return ck & (nibbles === 2n ? 0xffn : 0xfn); // 8-bit or 4-bit
};

// --- Public API ---

/**
 * Encodes IP and Port into a Base58 string.
 * IPv4 produces 9 chars, IPv6 produces 26 chars.
 */
export function encodeConnectionKey(input: ServerInfo): string {
  const isIPv6 = input.ip.includes(':');
  const bits = isIPv6 ? 144 : 48;
  const packed = isIPv6
    ? packIPv6(input.ip, input.port)
    : packIPv4(input.ip, input.port);

  const scrambled = feistel(packed, bits);
  const ckSize = isIPv6 ? 8n : 4n;
  const checksum = getChecksum(scrambled, bits);

  let combined = (scrambled << ckSize) | checksum;
  const targetLen = isIPv6 ? 26 : 9;

  let res = '';
  for (let i = 0; i < targetLen; i++) {
    res = ALPHABET[Number(combined % 58n)] + res;
    combined /= 58n;
  }
  return res;
}

/**
 * Decodes a Base58 string back into {ip, port}.
 * Returns undefined if the checksum or format is invalid.
 */
export function decodeConnectionKey(code: string): ServerInfo | undefined {
  if (code.length !== 9 && code.length !== 26) return undefined;

  const isIPv6 = code.length === 26;
  const bits = isIPv6 ? 144 : 48;
  const ckSize = isIPv6 ? 8n : 4n;

  let combined = 0n;
  for (const char of code) {
    const idx = ALPHABET.indexOf(char);
    if (idx === -1) return undefined;
    combined = combined * 58n + BigInt(idx);
  }

  const scrambled = combined >> ckSize;
  const checksum = combined & ((1n << ckSize) - 1n);

  if (getChecksum(scrambled, bits) !== checksum) return undefined;

  const original = feistel(scrambled, bits, true);
  return isIPv6 ? unpackIPv6(original) : unpackIPv4(original);
}
